/* Minimal Layout — quiet, lowercase, extreme whitespace.

   Identity vs Editorial / Bold:
   • Editorial = dark + soft serif + italic gold + cinematic photo hero.
   • Bold      = dark + colossal uppercase serif + magazine energy.
   • Minimal   = WARM LIGHT canvas + SANS-SERIF + lowercase + a lot of air.

   Best for newborn, family, fine art photographers — the work itself is
   the loudest thing in the room; the chrome whispers. */

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme, getSeason } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import IssueSeal from "@/components/editorial/IssueSeal";
import TenantWordmark from "@/components/tenant/TenantWordmark";

const NAV = [
  // Services is intentionally collapsed into Booking on Minimal — see
  // templates/minimal/Services.jsx (now a redirect stub). The visitor lands
  // straight on the only decision that matters: pick a sitting, pick a tier.
  { to: "/", label: "home", end: true },
  { to: "/portfolio", label: "work" },
  { to: "/booking", label: "enquire" },
  { to: "/contact", label: "contact" },
];

export default function MinimalLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 antialiased selection:bg-stone-900 selection:text-stone-50" style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
      {/* ── Top bar — barely there ─────────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-stone-50/85 backdrop-blur-md border-b border-stone-900/[0.06]" : "bg-transparent"
        }`}
        data-testid="minimal-topbar"
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
          <Link
            to={tenantHref("/")}
            className="text-[11px] tracking-[0.22em] text-stone-700 hover:text-stone-900 transition-colors"
            data-testid="minimal-wordmark"
          >
            <TenantWordmark logoUrl={settings?.logo_url} coverLogoUrl={settings?.cover_logo_url} tone="light" studioName={studioName} imgClassName="h-7 w-auto">
              {studioName.toLowerCase()}
            </TenantWordmark>
          </Link>
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {NAV.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`minimal-nav-${it.label}`}
                className={({ isActive }) =>
                  `text-[10px] tracking-[0.22em] transition-colors ${isActive ? "text-stone-900" : "text-stone-500 hover:text-stone-900"}`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>
          <Link
            to={tenantHref("/booking")}
            className="text-[10px] tracking-[0.22em] text-stone-500 hover:text-stone-900 transition-colors hidden md:inline"
            data-testid="minimal-book-link"
          >
            book →
          </Link>
        </div>
      </header>

      {/* Mobile inline nav under top bar (no overlay — keeps the quiet) */}
      <div className="md:hidden fixed top-20 inset-x-0 z-30 border-b border-stone-900/[0.06] bg-stone-50/95 backdrop-blur-md">
        <div className="px-6 py-3 flex items-center justify-between gap-3 text-[10px] tracking-[0.22em]">
          {NAV.slice(0, 4).map((it) => (
            <NavLink
              key={it.to}
              to={tenantHref(it.to)}
              end={it.end}
              data-testid={`minimal-mobile-nav-${it.label}`}
              className={({ isActive }) => (isActive ? "text-stone-900" : "text-stone-500")}
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main className="pt-32 md:pt-20" key={location.pathname}>
        <Outlet />
      </main>

      {/* ── Footer — single quiet line ─────────────────────────────────── */}
      <footer className="mt-32 lg:mt-44 py-16 border-t border-stone-900/[0.06]" data-testid="minimal-footer">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid sm:grid-cols-3 gap-8 items-end">
          <div>
            <div className="text-[10px] tracking-[0.22em] text-stone-500 mb-2">studio</div>
            <div className="text-sm text-stone-900">{studioName.toLowerCase()}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] tracking-[0.22em] text-stone-500 mb-2">contact</div>
            <div className="text-sm text-stone-900">{settings?.contact_email || "—"}</div>
          </div>
          <div className="text-right">
            <Link
              to="/admin/login"
              className="text-[10px] tracking-[0.22em] text-stone-500 hover:text-stone-900 transition-colors"
              data-testid="minimal-footer-admin"
            >
              studio manager →
            </Link>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 mt-12 text-[10px] tracking-[0.22em] text-stone-400 flex items-center justify-center gap-4">
          <span>&copy; {new Date().getFullYear()} {studioName.toLowerCase()}</span>
          <span className="opacity-50">·</span>
          <LumetierFooterCredit className="hover:text-stone-900" testid="minimal-lumetier-credit" />
        </div>
        {/* IssueSeal — small mark, centered footer signature */}
        <div className="max-w-[1280px] mx-auto mt-8 flex items-center justify-center opacity-50">
          <IssueSeal issue="01" season={getSeason()} year={new Date().getFullYear()} size={64} tone="charcoal" />
        </div>
      </footer>
      <TenantSignature tone="light" />
    </div>
  );
}
